import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import { LineChart } from "@mui/x-charts/LineChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    toNum,
} from "../../bids/analytics/format";
import { UeFilters, UeFiltersState } from "../components/UeFilters";
import { TermHint } from "../components/TermHint";
import {
    ueDepositQualityApi,
    ueNetFlowApi,
    uePaymentTakeRateApi,
} from "../unitEconomicsApi";
import { UeSeriesPoint } from "../../../models/unitEconomics";

type Props = {
    isActive: boolean;
    filters: UeFiltersState;
    onFiltersChange: (next: UeFiltersState) => void;
};

function mergeMoneySeries(parts: Array<{ key: string; points: UeSeriesPoint[] }>) {
    const map = new Map<number, Record<string, any>>();
    for (const s of parts) {
        for (const p of s.points ?? []) {
            const t = new Date(p.bucketUtc).getTime();
            const row = map.get(t) ?? { x: new Date(p.bucketUtc) };
            row[s.key] = toNum(p.value) / 100;
            map.set(t, row);
        }
    }
    return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v);
}

export function PaymentTakeRateTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => uePaymentTakeRateApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;

    const renderTable = (rows: typeof d extends null ? never : NonNullable<typeof d>["deposits"], empty: string) =>
        rows.length === 0 ? (
            <EmptyState hint={empty} />
        ) : (
            <ScrollTable>
                <Table size="sm" hoverRow>
                    <thead>
                        <tr>
                            <th>Метод</th>
                            <th style={{ textAlign: "right" }}>Кол-во</th>
                            <th style={{ textAlign: "right" }}>Оборот</th>
                            <th style={{ textAlign: "right" }}>
                                <TermHint term="ourBenefit" />
                            </th>
                            <th style={{ textAlign: "right" }}>
                                <TermHint term="takeRate">Take %</TermHint>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.paymentMethodId}>
                                <td>{r.title}</td>
                                <td style={{ textAlign: "right" }}>{formatInt(r.count)}</td>
                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.amount)}</td>
                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.benefit)}</td>
                                <td style={{ textAlign: "right" }}>{formatRatePercent(r.takeRate)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ScrollTable>
        );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="takeRate" /> по методам оплаты
                        </>
                    }
                    subtitle={
                        <>
                            <TermHint term="ourBenefit" /> / оборот по каждому методу депозитов и выводов.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Сравнивайте методы: где маржа платформы выше при сопоставимом объёме
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            <TermHint term="takeRate" /> метода
                                        </>
                                    ),
                                    ifGrows: "метод выгоднее для платформы (или дороже для пользователя).",
                                    ifFalls: "маржа метода сжимается — проверьте тарифы провайдера.",
                                },
                                {
                                    signal: "Оборот метода",
                                    ifGrows: "микс смещается к этому методу — смотрите его take %.",
                                    ifFalls: "метод теряет долю — возможно UX или лимиты.",
                                },
                            ]}
                        />
                    }
                >
                    <Typography level="title-sm">Депозиты</Typography>
                    {renderTable(d.deposits, "Нет депозитов за период.")}
                    <Typography level="title-sm" sx={{ mt: 1 }}>
                        Выводы
                    </Typography>
                    {renderTable(d.withdrawals, "Нет выводов за период.")}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function NetFlowTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueNetFlowApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;
    const chart = React.useMemo(() => {
        if (!d) return [];
        return mergeMoneySeries([
            { key: "deposits", points: d.depositSeries },
            { key: "withdrawals", points: d.withdrawalSeries },
            { key: "net", points: d.netFlowSeries },
            { key: "float", points: d.floatSeries },
        ]);
    }, [d]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title={
                        <>
                            <TermHint term="netFlow">Net Deposit Flow</TermHint> и{" "}
                            <TermHint term="float" />
                        </>
                    }
                    subtitle={
                        <>
                            Поток депозиты − выводы и подушка (
                            <TermHint term="float" /> = Tome + GMoney − Site).
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="netFlow" /> — чистый приток денег пользователей за период
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="float" /> — баланс у провайдеров минус обязательства сайта
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHint term="netFlow" />,
                                    ifGrows: "в систему входит больше денег, чем уходит — рост float/балансов.",
                                    ifFalls: "отток (выводы > депозиты) — следите за ликвидностью.",
                                },
                                {
                                    signal: <TermHint term="float" />,
                                    ifGrows: "запас у провайдеров относительно обязательств сайта растёт.",
                                    ifFalls: "подушка сжимается — риск кассовых разрывов.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint label="Депозиты" value={formatMoneyKopecks(d.depositSum)} color="success" />
                        <MetricHint label="Выводы" value={formatMoneyKopecks(d.withdrawalSum)} />
                        <MetricHint
                            label={<TermHint term="netFlow" />}
                            value={formatMoneyKopecks(d.netFlow)}
                            color={d.netFlow >= 0 ? "success" : "danger"}
                        />
                        <MetricHint
                            label={<TermHint term="float" />}
                            value={formatMoneyKopecks(d.float)}
                            color="primary"
                        />
                        <MetricHint
                            label="Балансы"
                            value={formatMoneyKopecks(d.siteBalance)}
                            hint={`Tome ${formatMoneyKopecks(d.tomeBalance)} · GM ${formatMoneyKopecks(d.gameMoneyBalance)}`}
                        />
                    </Box>
                    {chart.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <LineChart
                            height={280}
                            dataset={chart}
                            xAxis={[{ dataKey: "x", scaleType: "time" }]}
                            series={[
                                { dataKey: "deposits", label: "Депозиты ₽", color: "#2e7d32" },
                                { dataKey: "withdrawals", label: "Выводы ₽", color: "#c62828" },
                                { dataKey: "net", label: "Net flow ₽", color: "#1565c0" },
                                { dataKey: "float", label: "Float ₽", color: "#6a1b9a" },
                            ]}
                            margin={{ left: 70, right: 20, top: 20, bottom: 40 }}
                        />
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

export function DepositQualityTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueDepositQualityApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );
    const d = state.data;
    const chart = React.useMemo(() => {
        if (!d) return [];
        const map = new Map<number, Record<string, any>>();
        const add = (points: UeSeriesPoint[], key: string) => {
            for (const p of points ?? []) {
                const t = new Date(p.bucketUtc).getTime();
                const row = map.get(t) ?? { x: new Date(p.bucketUtc) };
                row[key] = toNum(p.count);
                map.set(t, row);
            }
        };
        add(d.paidSeries, "paid");
        add(d.chargebackSeries, "chargeback");
        add(d.refundSeries, "refund");
        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
    }, [d]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title="Качество депозитов"
                    subtitle={
                        <>
                            <TermHint term="paid" /> / Failed / <TermHint term="chargeback" /> /{" "}
                            <TermHint term="refund" /> среди созданных депозитов и потери.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="paid" /> rate — доля успешно оплаченных от созданных счетов
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    В таблице по методам колонка Take % здесь показывает долю{" "}
                                    <TermHint term="chargeback" /> в статусах метода
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHint term="paid" />,
                                    ifGrows: "больше депозитов успешно завершаются.",
                                    ifFalls: "трение оплаты или отказы — проверьте методы.",
                                },
                                {
                                    signal: (
                                        <>
                                            <TermHint term="chargeback" /> / <TermHint term="refund" />
                                        </>
                                    ),
                                    growsIsGood: false,
                                    ifGrows: "растут возвраты и потери — риск и давление на P&L.",
                                    ifFalls: "качество платежей улучшается.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint label="Создано" value={formatInt(d.totalCreated)} />
                        <MetricHint
                            label={<TermHint term="paid" />}
                            value={formatInt(d.paidCount)}
                            hint={formatRatePercent(d.paidRate)}
                            color="success"
                        />
                        <MetricHint label="Failed" value={formatInt(d.failedCount)} color="warning" />
                        <MetricHint
                            label={<TermHint term="chargeback" />}
                            value={formatInt(d.chargebackCount)}
                            hint={formatRatePercent(d.chargebackRate)}
                            color="danger"
                        />
                        <MetricHint
                            label={<TermHint term="refund" />}
                            value={formatInt(d.refundCount)}
                            color="danger"
                        />
                        <MetricHint
                            label={
                                <>
                                    Потери <TermHint term="chargeback">CB</TermHint>
                                </>
                            }
                            value={formatMoneyKopecks(d.chargebackLoss)}
                            color="danger"
                        />
                        <MetricHint
                            label={
                                <>
                                    Потери <TermHint term="refund" />
                                </>
                            }
                            value={formatMoneyKopecks(d.refundLoss)}
                        />
                    </Box>
                    {chart.length > 0 && (
                        <LineChart
                            height={260}
                            dataset={chart}
                            xAxis={[{ dataKey: "x", scaleType: "time" }]}
                            series={[
                                { dataKey: "paid", label: "Paid", color: "#2e7d32" },
                                { dataKey: "chargeback", label: "Chargeback", color: "#c62828" },
                                { dataKey: "refund", label: "Refund", color: "#ed6c02" },
                            ]}
                            margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                        />
                    )}
                    {d.byMethod.length > 0 && (
                        <>
                            <Typography level="title-sm">По методам</Typography>
                            <ScrollTable>
                                <Table size="sm" hoverRow>
                                    <thead>
                                        <tr>
                                            <th>Метод</th>
                                            <th style={{ textAlign: "right" }}>Кол-во</th>
                                            <th style={{ textAlign: "right" }}>Сумма</th>
                                            <th style={{ textAlign: "right" }}>Benefit Paid</th>
                                            <th style={{ textAlign: "right" }}>Доля CB</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.byMethod.map((r) => (
                                            <tr key={r.paymentMethodId}>
                                                <td>{r.title}</td>
                                                <td style={{ textAlign: "right" }}>{formatInt(r.count)}</td>
                                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.amount)}</td>
                                                <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.benefit)}</td>
                                                <td style={{ textAlign: "right" }}>{formatRatePercent(r.takeRate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </ScrollTable>
                        </>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}
